/**
 * TepOverlayDisplayObject2D — Renders a simulated TEP (PET) volume on 2D slices.
 *
 * Uses a scintigraphy colormap (Black → Teal → Purple → Orange → White) with
 * transparent background for zero-value voxels. Renders as a vtkImageSlice on
 * renderer layer 1 (overlay).
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkImageResliceMapper from '@kitware/vtk.js/Rendering/Core/ImageResliceMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import type vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import { getScintigraphyColormap } from '../../../workflows/oncology/dosimetry/domain/scintigraphy-colormap';
import { BaseViewport } from '../../viewports';
import { SliceViewport2D } from '../../viewports/slice-viewport-2d';
import type {
    DisplayFeatureType,
    IDisplayFeature,
    IDisplayObject,
    TepOverlayDisplayData,
    TepOverlayDisplayProperties,
    ViewMode,
} from '../types';

export class TepOverlayDisplayObject2D implements IDisplayObject {
    readonly feature: IDisplayFeature<DisplayFeatureType.TEP_OVERLAY>;
    readonly viewMode: ViewMode;

    private viewport: BaseViewport | null = null;
    private renderer: vtkRenderer | null = null;

    private imageSlice: vtkImageSlice | null = null;
    private resliceMapper: vtkImageResliceMapper | null = null;
    private colorFunction: vtkColorTransferFunction | null = null;
    private opacityFunction: vtkPiecewiseFunction | null = null;

    /** Track the vtkImageData reference to detect changes in update() */
    private currentTepVolume: vtkImageData | null = null;

    constructor(feature: IDisplayFeature<DisplayFeatureType.TEP_OVERLAY>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
    }

    private get data(): TepOverlayDisplayData {
        return this.feature.data;
    }

    private get props(): TepOverlayDisplayProperties {
        return this.feature.properties;
    }

    initialize(viewport: BaseViewport): void {
        this.viewport = viewport;
        this.renderer = viewport.getRenderer(1);

        this.createPipeline();
    }

    update(): void {
        const tepVolume = this.data.tepVolume;

        // If the TEP volume reference changed, rebuild the pipeline
        if (tepVolume !== this.currentTepVolume) {
            this.disposePipeline();
            if (tepVolume) {
                this.createPipeline();
            }
            return;
        }

        // Otherwise rebuild transfer functions (e.g. opacity changed)
        if (this.opacityFunction && this.currentTepVolume) {
            this.buildTransferFunctions();
            this.viewport?.render();
        }
    }

    dispose(): void {
        this.disposePipeline();
        this.renderer = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        return [];
    }

    // ── Pipeline ──

    private createPipeline(): void {
        if (!this.renderer || !this.viewport) {
            console.warn('[TepOverlayDisplayObject2D] createPipeline called before initialize');
            return;
        }

        const tepVolume = this.data.tepVolume;
        if (!tepVolume) return;

        const slicePlane = this.viewport instanceof SliceViewport2D ? this.viewport.getSlicePlane() : null;
        if (!slicePlane) {
            console.warn('[TepOverlayDisplayObject2D] createPipeline: no slice plane available');
            return;
        }

        this.currentTepVolume = tepVolume;

        this.resliceMapper = vtkImageResliceMapper.newInstance();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.resliceMapper as any).setInputData(tepVolume);
        this.resliceMapper.setSlicePlane(slicePlane);
        this.resliceMapper.setBackgroundColor([0, 0, 0, 0]);

        this.colorFunction = vtkColorTransferFunction.newInstance();
        this.opacityFunction = vtkPiecewiseFunction.newInstance();
        this.buildTransferFunctions();

        this.imageSlice = vtkImageSlice.newInstance();
        this.imageSlice.setMapper(this.resliceMapper);

        const property = this.imageSlice.getProperty();
        property.setRGBTransferFunction(0, this.colorFunction);
        property.setScalarOpacity(0, this.opacityFunction);
        property.setInterpolationTypeToLinear();
        property.setUseLookupTableScalarRange(true);
        property.setIndependentComponents(true);

        this.renderer.addActor(this.imageSlice);
        this.viewport?.render();
    }

    private buildTransferFunctions(): void {
        if (!this.colorFunction || !this.opacityFunction || !this.currentTepVolume) return;

        const scalars = this.currentTepVolume.getPointData().getScalars();
        if (!scalars) {
            console.warn('[TepOverlayDisplayObject2D] TEP volume has no scalar data');
            return;
        }

        this.colorFunction.removeAllPoints();
        this.opacityFunction.removeAllPoints();

        const range = scalars.getRange();
        const min: number = range[0];
        const max: number = range[1];

        if (min >= max) {
            this.colorFunction.addRGBPoint(0, 0, 0, 0);
            this.opacityFunction.addPoint(0, 0);
            return;
        }

        const lut = getScintigraphyColormap();

        // Background (value = min) is transparent
        this.opacityFunction.addPoint(min, 0);

        // Map the 256 LUT entries across [min, max]
        // Opacity ramps from transparent (i=0) to props.opacity (i=255)
        for (let i = 0; i < 256; i++) {
            const scalar = min + (i / 255) * (max - min);
            const [r, g, b] = lut[i];
            this.colorFunction.addRGBPoint(scalar, r / 255, g / 255, b / 255);

            const alpha = Math.min((i * 4) / 255, 1.0) * this.props.opacity;
            this.opacityFunction.addPoint(scalar, alpha);
        }
    }

    private disposePipeline(): void {
        if (this.renderer && this.imageSlice) {
            this.renderer.removeActor(this.imageSlice);
        }
        this.imageSlice?.delete();
        this.resliceMapper?.delete();
        this.colorFunction?.delete();
        this.opacityFunction?.delete();

        this.imageSlice = null;
        this.resliceMapper = null;
        this.colorFunction = null;
        this.opacityFunction = null;
        this.currentTepVolume = null;
    }
}
